import {
  MarketplacePaymentState,
  SalesGroupDetails,
} from 'src/WebApiController';

/**
 * Outstanding balance for the group, computed from components so it stays correct
 * across paid/partial/unpaid — and, unlike the backend `balanceDue`, includes tax.
 *
 * owed = subtotal + Σ earningLines − paymentReceived − refunds
 *      = subtotal + charges − credits + tax − paymentReceived − refunds.
 *
 * The earning lines carry charges, credits AND tax (backend maps
 * MarketplacePaymentLineType.Tax → SaleCostType.Taxes), so summing them folds tax
 * in as a positive amount. We deliberately do NOT prefer the backend `balanceDue`:
 * it is built from Sale.Charges/Credits (tax-blind) and is clamped at 0, so
 * `balanceDue + tax` would wrongly show tax as owed on a group that already paid it.
 *
 * POS-8856: earning-line amounts have an INCONSISTENT upstream sign across paths,
 * so magnitude-normalize each with Math.abs and re-apply the sign from the
 * authoritative isCredit flag (charge/tax +, credit −).
 */
export function getOutstandingAmt(group: SalesGroupDetails): number {
  // POS-8726: Account for refunded/reversed lines. When a payment is refunded,
  // the backend's totalPaymentReceived doesn't include the refunded amount. But
  // the refund is intentional — the seller chose to give the money back, so the
  // refunded amount should count as "settled" and reduce the balance.
  const refundedTotal = (group.aggregatedPaymentLines ?? [])
    .filter(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    )
    .reduce((sum, l) => sum + (l.amt?.amt ?? 0), 0);

  const earningsSigned = (group.earningLines ?? []).reduce(
    (sum, line) =>
      sum + (line.isCredit ? -1 : 1) * Math.abs(line.amount?.amt ?? 0),
    0
  );
  const owedBase = (group.subtotal?.amt ?? 0) + earningsSigned;
  const received = group.totalPaymentReceived?.amt ?? 0;

  return Math.max(0, owedBase - received - refundedTotal);
}
